/**
 * Options for the filter actions composable
 */
export interface IUseHFilterActionsOptions {
  filters: Record<string, any>;
  onFilterChange?: () => void;
}

/**
 * Composable for managing filter actions (toggle, check active state)
 *
 * @example
 * const filters = reactive({ name: '', tag: [], domain: [] });
 * const { isFilterActive, toggleFilter, toggleArrayFilter } = useHFilterActions({
 *   filters,
 *   onFilterChange: () => fetchData()
 * });
 *
 * // Check if filter is active
 * if (isFilterActive('name', 'John', 'exact')) {
 *   // ...
 * }
 *
 * // Toggle a simple filter
 * toggleFilter('name', 'John', 'exact');
 *
 * // Toggle an array filter
 * toggleArrayFilter('tag', 'admin');
 */
export function useHFilterActions(options: IUseHFilterActionsOptions) {
  const { filters, onFilterChange } = options;

  /**
   * Normalize a value for comparison
   */
  function normalizeValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return value.toString().trim().toLowerCase();
  }

  /**
   * Check if a filter is currently active
   *
   * @param filterKey - The filter key to check
   * @param value - The value to check for
   * @param matchType - Optional match type for string filters (e.g., 'exact', 'contains')
   * @returns True if the filter is active
   */
  function isFilterActive(filterKey: string, value: any, matchType: string | null = null): boolean {
    // Return false if value is null or undefined
    if (value === null || value === undefined) {
      return false;
    }

    // Handle string filters with match type
    if (matchType && (filterKey === 'name' || filterKey === 'key')) {
      return filters[filterKey] === value && filters[`${filterKey}MatchType`] === matchType;
    }

    // Handle array filters (tags, domains, etc.)
    if (Array.isArray(filters[filterKey])) {
      const normalizedValue = normalizeValue(value);
      return filters[filterKey].some((item: any) => normalizeValue(item) === normalizedValue);
    }

    // Handle simple string filters
    return filters[filterKey] === value;
  }

  /**
   * Toggle a simple or string filter on/off
   *
   * @param filterKey - The filter key to toggle
   * @param value - The value to set/unset
   * @param matchType - Optional match type for string filters (e.g., 'exact', 'contains')
   */
  function toggleFilter(filterKey: string, value: any, matchType: string | null = null): void {
    if (filterKey === 'name' || filterKey === 'key') {
      if (filters[filterKey] === value && (!matchType || filters[`${filterKey}MatchType`] === matchType)) {
        filters[filterKey] = '';
        filters[`${filterKey}MatchType`] = '';
      } else {
        filters[filterKey] = value;
        if (matchType) filters[`${filterKey}MatchType`] = matchType;
      }
    } else {
      filters[filterKey] = filters[filterKey] === value ? '' : value;
    }

    // Reset to first page when filter changes
    if (filters.page !== undefined) {
      filters.page = 1;
    }

    // Call the optional callback
    if (onFilterChange) {
      onFilterChange();
    }
  }

  /**
   * Toggle an item in an array filter
   *
   * @param filterKey - The filter key to toggle
   * @param value - The value to add/remove from the array
   */
  function toggleArrayFilter(filterKey: string, value: any): void {
    // Do nothing if value is null or undefined
    if (value === null || value === undefined) {
      return;
    }

    const values = filters[filterKey] || [];
    const index = values.indexOf(value);
    if (index > -1) {
      filters[filterKey] = values.filter((_: any, itemIndex: number) => itemIndex !== index);
    } else {
      filters[filterKey] = [...values, value];
    }

    // Reset to first page when filter changes
    if (filters.page !== undefined) {
      filters.page = 1;
    }

    // Call the optional callback
    if (onFilterChange) {
      onFilterChange();
    }
  }

  return {
    isFilterActive,
    toggleFilter,
    toggleArrayFilter,
  };
}
